import { useState, useEffect } from "react";
import { SandboxOutputLine } from "./OutputTypes";

const useOutputInternal = (replUrl: string | null, isReady: boolean) => {
  const [outputLines, setOutputLines] = useState<SandboxOutputLine[]>([]);
  const [newLinesCount, setNewLinesCount] = useState<number>(0);

  useEffect(() => {
    // Only create EventSource when we have a URL AND the sandbox is ready
    if (!replUrl || !isReady) {
      return;
    }

    // Create EventSource on mount
    const source = new EventSource(`${replUrl}/repl/eval`, { withCredentials: true });

    source.onerror = () => {
      const readyStateMap = { 0: "CONNECTING", 1: "OPEN", 2: "CLOSED" } as const;
      const state = readyStateMap[source.readyState as keyof typeof readyStateMap] || "UNKNOWN";
      console.error(`[OutputHook] EventSource error (readyState: ${state}, url: ${replUrl})`);
    };

    const onMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as { output: string; error?: boolean };
      if (typeof data.output === "string") {
        let newLinesCount = 0;
        for (const line of data.output.split("\n")) {
          if (line.trim() === "") {
            continue;
          }
          setOutputLines(prev => [
            ...prev,
            {
              id: (prev.length > 0 ? parseInt(prev[prev.length - 1].id) + 1 : 1).toString(),
              timestamp: new Date(),
              text: line,
            },
          ]);
          newLinesCount++;
        }

        if (newLinesCount > 0) {
          setNewLinesCount(prev => prev + newLinesCount);
        }
      }
    };

    source.addEventListener("repl-errors", onMessage);

    // Cleanup: remove listener and close EventSource on unmount
    return () => {
      source.removeEventListener("repl-errors", onMessage);
      source.close();
    };
  }, [replUrl, isReady]);

  const resetNewLinesCount = () => {
    setNewLinesCount(0);
  };

  const clearOutput = () => {
    setOutputLines([]);
    setNewLinesCount(0);
  };

  return { outputLines, newLinesCount, resetNewLinesCount, clearOutput };
};

export default useOutputInternal;
